// src/context/AuthContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { userPool, CognitoAuthService } from "../services/cognitoService";
import { UserGroup } from "../types/UserGroup";
import { useTokenRefresh } from "../hooks/useTokenRefresh";
import { setStoredIdToken, clearStoredIdToken } from "../auth/tokenStore";
import { debug, formatSafeErrorMessage } from "../utils/debugLogger";
import {
  notifySessionUnauthorized,
  clearSessionExpiredPrompt,
} from "../auth/sessionUnauthorizedEvents";
import { clearPersistedWorkspace } from "../utils/workspacePersistence";

interface AuthContextType {
  isAuthenticated: boolean;
  user: string; // This will now be the display name
  userId: string; // Added userId to store the actual ID
  idToken: string;
  userGroups: UserGroup[]; // Optional user group
  login: (email: string, password: string) => Promise<void>;
  completeNewPassword: (email: string, newPassword: string) => Promise<void>;
  completeMfaChallenge: (email: string, code: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // State management
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<string>(""); // Display name
  const [userId, setUserId] = useState<string>(""); // User ID/email
  const [idToken, setIdToken] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [userGroups, setUserGroups] = useState<UserGroup[]>([]); // User's groups
  const [authError, setAuthError] = useState<string | null>(null);
  // Initialize auth state
  useEffect(() => {
    initializeAuthState();
  }, []);

  // Mirror real auth-state token changes (login / init / recovery) into the
  // module token store. Routine silent refreshes update the store directly via
  // useTokenRefresh and never flow through React state, so they don't land here.
  useEffect(() => {
    if (idToken) {
      setStoredIdToken(idToken);
    }
  }, [idToken]);

  /**
   * Initialize authentication state from session or localStorage
   */
  const initializeAuthState = async () => {
    try {
      // First try to get current session
      const session = await CognitoAuthService.getCurrentSession();

      if (session) {
        // User has a valid session
        setIsAuthenticated(true);
        setUserId(session.username);
        setUser(session.displayName);
        setIdToken(session.idToken);
        // Extract and set user groups
        const payload = CognitoAuthService.parseTokenPayload(session.idToken);
        setUserGroups(extractUserGroups(payload));
        return;
      }

      // No session, try localStorage
      const storedToken = localStorage.getItem("idToken");
      if (!storedToken) {
        clearAuthState();
        return;
      }

      if (CognitoAuthService.isTokenExpired(storedToken)) {
        setAuthError("Session expired. Please log in again.");
        notifySessionUnauthorized(401);
        clearAuthState();
        return;
      }

      // Valid stored token
      setIsAuthenticated(true);
      setIdToken(storedToken);

      try {
        const payload = CognitoAuthService.parseTokenPayload(storedToken);
        setUserId(payload["cognito:username"] || payload.sub || "");
        setUser(CognitoAuthService.extractDisplayName(payload));
        setUserGroups(extractUserGroups(payload));
      } catch (error) {
        debug.error("Error parsing token:", formatSafeErrorMessage(error));
        clearAuthState();
      }
    } catch (error) {
      debug.error("Auth initialization error:", formatSafeErrorMessage(error));
      clearAuthState();
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Clear all authentication state
   */
  const clearAuthState = useCallback(() => {
    setIsAuthenticated(false);
    setUser("");
    setUserId("");
    setIdToken("");
    setUserGroups([]);
    clearStoredIdToken();
  }, []);

  // Recovery: if authenticated but idToken is missing, re-fetch from Cognito session
  useEffect(() => {
    if (!isAuthenticated || idToken || isLoading) return;

    const recoverToken = async () => {
      try {
        const session = await CognitoAuthService.getCurrentSession();
        if (session) {
          setIdToken(session.idToken);
          setUserId(session.username);
          setUser(session.displayName);
          const payload = CognitoAuthService.parseTokenPayload(session.idToken);
          setUserGroups(extractUserGroups(payload));
        } else {
          debug.warn("Token recovery failed: no valid session");
          notifySessionUnauthorized(401);
          clearAuthState();
        }
      } catch (error) {
        debug.error("Token recovery error:", formatSafeErrorMessage(error));
        notifySessionUnauthorized(401);
        clearAuthState();
      }
    };

    recoverToken();
  }, [isAuthenticated, idToken, isLoading]);

  // Automatically refresh token shortly before expiration (expiry-based, not
  // polling). Routine refreshes update the module token store only — they do NOT
  // call setIdToken, so the provider value below stays referentially stable and
  // the app-wide idToken-keyed effects don't re-fire on a silent refresh.
  useTokenRefresh({
    isAuthenticated,
    idToken,
    clearAuthState,
  });

  /**
   * Handle user login
   */
  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      try {
        const result = await CognitoAuthService.login(email, password);

        setIsAuthenticated(true);
        setUserId(email);
        setUser(result.displayName);
        setIdToken(result.idToken);
        clearSessionExpiredPrompt();
        // Extract user groups from token payload
        const payload = CognitoAuthService.parseTokenPayload(result.idToken);
        setUserGroups(extractUserGroups(payload));
      } catch (error) {
        const err = error as any;
        if (err.code === "NewPasswordRequired") {
          // Propagate this info so your UI can show the new password form
          return Promise.reject({ ...err, newPasswordRequired: true, email });
        }
        if (err.code === "SoftwareTokenMfaRequired") {
          // Propagate this info so your UI can show the MFA challenge form
          return Promise.reject({ ...err, mfaRequired: true, email });
        }
        return Promise.reject(error);
      }
    },
    []
  );

  /**
   * Complete new password challenge and update auth state
   */
  const completeNewPassword = useCallback(
    async (email: string, newPassword: string): Promise<void> => {
      const result = await CognitoAuthService.completeNewPasswordChallenge(
        email,
        newPassword
      );
      setIsAuthenticated(true);
      setUserId(email);
      setUser(result.displayName);
      setIdToken(result.idToken);
      clearSessionExpiredPrompt();
      const payload = CognitoAuthService.parseTokenPayload(result.idToken);
      setUserGroups(extractUserGroups(payload));
    },
    []
  );

  /**
   * Complete MFA challenge and update auth state
   */
  const completeMfaChallenge = useCallback(
    async (email: string, code: string): Promise<void> => {
      const result = await CognitoAuthService.completeMfaChallenge(email, code);
      setIsAuthenticated(true);
      setUserId(email);
      setUser(result.displayName);
      setIdToken(result.idToken);
      clearSessionExpiredPrompt();
      const payload = CognitoAuthService.parseTokenPayload(result.idToken);
      setUserGroups(extractUserGroups(payload));
    },
    []
  );

  /**
   * Handle user logout
   */
  const logout = useCallback(() => {
    CognitoAuthService.logout();
    clearPersistedWorkspace();
    clearAuthState();
  }, [clearAuthState]);

  // Memoize the context value so it only changes on real auth events. A fresh
  // object every render would defeat the stability the rest of this change buys.
  const value = useMemo(
    () => ({
      isAuthenticated,
      user,
      userId,
      idToken,
      userGroups,
      login,
      completeNewPassword,
      completeMfaChallenge,
      logout,
      isLoading,
    }),
    [
      isAuthenticated,
      user,
      userId,
      idToken,
      userGroups,
      login,
      completeNewPassword,
      completeMfaChallenge,
      logout,
      isLoading,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

/**
 * Extract groups from token payload
 */
const extractUserGroups = (payload: any): UserGroup[] => {
  try {
    // Check for cognito:groups claim in token
    const groups = payload["cognito:groups"] || [];

    // Map string groups to UserGroup enum values if they match
    return groups
      .filter((group: string) =>
        Object.values(UserGroup).includes(group as UserGroup)
      )
      .map((group: string) => group as UserGroup);
  } catch (error) {
    debug.error("Error extracting user groups:", formatSafeErrorMessage(error));
    return [];
  }
};
